/**
 * usePickerFilterCheck (ORCH-400 §4.6, §4.9)
 *
 * Purpose: Asks the server which of a form's selected list values would be refused on
 *          save (security scope, the tenant's enabled lists, related/subset filters), and
 *          which stored values are locked for this user (outside their access, so kept
 *          but not removable). The form shows the failures as standard field errors and
 *          blocks Save; the pickers leave the delete icon off locked chips.
 * Logic: One React Query keyed by the model, the permission, the record and the sorted
 *        list-field values, so it refetches when a list field changes and never for a
 *        text field. Nothing selected means nothing to check and no request. The
 *        previous answer is kept while the next one loads, so errors don't flicker.
 * Dependencies: metadataService.validatePickerFilters, PickerAncestors (the ancestor
 *               records a child form is opened under), asList (ORCH-354)
 */

import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { metadataService, type PickerFilterCheck } from '../services/metadataService';
import { usePickerAncestors } from '../context/PickerScopeContext';
import { asList } from '../utils/listValues';
import type { PermissionKey } from '../constants/permissionKeys';

const NO_ERRORS: Record<string, string> = {};
const NO_LOCKED: Record<string, string[]> = {};

export interface PickerFilterCheckOptions {
  modelId: string;
  tenantId: string;
  /** Form values keyed by fielddef_id (list fields are read with asList) */
  values: Record<string, unknown>;
  /** The fielddef_ids of the form's list fields; others are never sent */
  listFieldIds: readonly string[];
  /** The write the form will make; null for an unscoped form */
  permission: PermissionKey | null;
  /** The record an edit form opened; its stored values decide what is locked */
  recordId?: string | null;
  enabled?: boolean;
}

export interface PickerFilterCheckResult {
  /** fielddef_id -> the message to show as the field's error; empty when all match */
  errors: Record<string, string>;
  /** fielddef_id -> stored ids this user can't remove */
  locked: Record<string, string[]>;
  isFetching: boolean;
}

/** The list values worth sending, sorted so equal selections give one query key */
export const listValuesToCheck = (
  values: Record<string, unknown>,
  listFieldIds: readonly string[]
): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  [...listFieldIds].sort().forEach((id) => {
    const ids = asList(values[id]).map(String);
    if (ids.length) out[id] = ids;
  });
  return out;
};

/**
 * The server's message as a field error: the field label prefix the API's 400 carries
 * ("Target Audience: ...") is dropped, because the field already shows its label, and a
 * mismatch the user can fix says how.
 */
export const fieldErrorText = (failure: { message: string; reason: string }): string => {
  const text = failure.message.replace(/^[^:]+: /, '');
  if (failure.reason === 'security_removed') return text;
  return /[.!]$/.test(text) ? `${text} Remove it to save.` : `${text}. Remove it to save.`;
};

const childMessage = (count: number, childModelId: string): string =>
  `${count} ${childModelId} record${count === 1 ? '' : 's'} use a value this change removes. Change them first.`;

export const usePickerFilterCheck = (
  options: PickerFilterCheckOptions
): PickerFilterCheckResult => {
  const { modelId, tenantId, values, listFieldIds, permission, recordId, enabled } = options;
  const ancestors = usePickerAncestors();
  const toCheck = useMemo(() => listValuesToCheck(values, listFieldIds), [values, listFieldIds]);
  const valuesKey = JSON.stringify(toCheck);
  const ancestorsKey = JSON.stringify(ancestors);
  const hasSelection = valuesKey !== '{}';

  const query = useQuery<PickerFilterCheck>({
    queryKey: [
      'metadata',
      'picker-filters',
      'check',
      modelId,
      tenantId,
      permission,
      recordId ?? null,
      valuesKey,
      ancestorsKey,
    ],
    queryFn: () =>
      metadataService.validatePickerFilters(modelId, {
        values: toCheck,
        permission,
        record_id: recordId ?? null,
        ...(Object.keys(ancestors).length ? { ancestors } : {}),
      }),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled: enabled !== false && !!modelId && !!tenantId && hasSelection,
  });

  const data = hasSelection ? query.data : undefined;
  const errors = useMemo(() => {
    if (!data) return NO_ERRORS;
    const out: Record<string, string> = {};
    Object.entries(data.fields ?? {}).forEach(([fielddefId, failure]) => {
      out[fielddefId] = fieldErrorText(failure);
    });
    Object.entries(data.children ?? {}).forEach(([fielddefId, broken]) => {
      if (!out[fielddefId]) out[fielddefId] = childMessage(broken.count, broken.child_model_id);
    });
    return Object.keys(out).length ? out : NO_ERRORS;
  }, [data]);
  const locked = useMemo(() => {
    if (!data?.locked || !Object.keys(data.locked).length) return NO_LOCKED;
    return data.locked;
  }, [data]);

  return { errors, locked, isFetching: hasSelection && query.isFetching };
};
